import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = {
  name: '',
  code: '',
  description: '',
  currency: 'UGX',
  monthly_price: 0,
  yearly_price: 0,
  trial_days: 14,
  is_default: false,
  is_active: true,
  can_create_store: true,
  can_create_vcard: true,
  can_create_invoices: false,
  can_create_quotations: false,
  can_use_crm: false,
  can_use_payment_reminders: false,
  can_use_subscriptions: false,
  can_use_budget: false,
  can_use_business_profile: false,
  can_use_qr_codes: false,
  can_use_pos: false,
  can_use_receipts: false,
  can_use_delivery_options: false,
  can_use_analytics: false,
  can_use_staff_accounts: false,
  can_use_whatsapp_templates: false,
  can_use_bulk_payment_links: false,
  can_use_marketplace: false,
  product_limit: 5,
  invoice_limit_per_month: 0,
  customer_limit: 0,
  staff_limit: 0,
  max_branches: 1,
  max_transactions_per_month: 0,
  max_loans: 0,
  max_credit_records: 0,
  advanced_insights_enabled: false,
  reports_enabled: false,
  export_enabled: false,
  custom_link_limit: 0,
  custom_domain_allowed: false,
  remove_branding_allowed: false,
  whatsapp_orders_allowed: true,
  online_payments_allowed: true,
  cash_on_delivery_allowed: true,
  priority_support_label: '',
  active: true,
};

export default function PackagesAdmin() {
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/packages');
      setPackages(r.data.packages || []);
    } catch {
      toast.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/packages/${editing.id}`, form);
        toast.success('Package updated');
      } else {
        await api.post('/admin/packages', form);
        toast.success('Package created');
      }
      setForm(EMPTY);
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save package');
    } finally {
      setSaving(false);
    }
  };

  const edit = (pkg) => {
    setEditing(pkg);
      setForm({
      name: pkg.name || '',
      code: pkg.code || '',
      description: pkg.description || '',
      currency: pkg.currency || 'UGX',
      monthly_price: pkg.monthly_price || 0,
      yearly_price: pkg.yearly_price || 0,
      trial_days: pkg.trial_days || 14,
      is_default: !!pkg.is_default,
      is_active: pkg.is_active !== false && pkg.active !== false,
      can_create_store: !!pkg.can_create_store,
      can_create_vcard: !!pkg.can_create_vcard,
      can_create_invoices: !!pkg.can_create_invoices,
      can_create_quotations: !!pkg.can_create_quotations,
      can_use_crm: !!pkg.can_use_crm,
      can_use_payment_reminders: !!pkg.can_use_payment_reminders,
      can_use_subscriptions: !!pkg.can_use_subscriptions,
      can_use_budget: !!pkg.can_use_budget,
      can_use_business_profile: !!pkg.can_use_business_profile,
      can_use_qr_codes: !!pkg.can_use_qr_codes,
      can_use_pos: !!pkg.can_use_pos,
      can_use_receipts: !!pkg.can_use_receipts,
      can_use_delivery_options: !!pkg.can_use_delivery_options,
      can_use_analytics: !!pkg.can_use_analytics,
      can_use_staff_accounts: !!pkg.can_use_staff_accounts,
      can_use_whatsapp_templates: !!pkg.can_use_whatsapp_templates,
      can_use_bulk_payment_links: !!pkg.can_use_bulk_payment_links,
      can_use_marketplace: !!pkg.can_use_marketplace,
      product_limit: pkg.product_limit || 0,
      invoice_limit_per_month: pkg.invoice_limit_per_month || 0,
      customer_limit: pkg.customer_limit || 0,
      staff_limit: pkg.staff_limit || 0,
      max_branches: pkg.max_branches || 0,
      max_transactions_per_month: pkg.max_transactions_per_month || 0,
      max_loans: pkg.max_loans || 0,
      max_credit_records: pkg.max_credit_records || 0,
      advanced_insights_enabled: !!pkg.advanced_insights_enabled,
      reports_enabled: !!pkg.reports_enabled,
      export_enabled: !!pkg.export_enabled,
      custom_link_limit: pkg.custom_link_limit || 0,
      custom_domain_allowed: !!pkg.custom_domain_allowed,
      remove_branding_allowed: !!pkg.remove_branding_allowed,
      whatsapp_orders_allowed: !!pkg.whatsapp_orders_allowed,
      online_payments_allowed: !!pkg.online_payments_allowed,
      cash_on_delivery_allowed: !!pkg.cash_on_delivery_allowed,
      priority_support_label: pkg.priority_support_label || '',
      active: pkg.active !== false,
    });
  };

  const disable = async (pkg) => {
    if (!window.confirm(`Disable package "${pkg.name}"?`)) return;
    try {
      await api.delete(`/admin/packages/${pkg.id}`);
      toast.success('Package disabled');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to disable package');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>Packages</h1>
        <p className="text-slate-500 text-sm mt-0.5">Create and manage subscription plans for store and vCard access.</p>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>{editing ? 'Edit Package' : 'Create Package'}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="grid gap-4 md:grid-cols-2">
            {[
              ['Package Name', 'name'],
              ['Package Code', 'code'],
              ['Description', 'description'],
              ['Currency', 'currency'],
              ['Monthly Price', 'monthly_price'],
              ['Yearly Price', 'yearly_price'],
              ['Trial Days', 'trial_days'],
              ['Product Limit', 'product_limit'],
              ['Invoice Limit / Month', 'invoice_limit_per_month'],
              ['Customer Limit', 'customer_limit'],
              ['Staff Limit', 'staff_limit'],
              ['Max Branches', 'max_branches'],
              ['Max Transactions / Month', 'max_transactions_per_month'],
              ['Max Loans', 'max_loans'],
              ['Max Credit Records', 'max_credit_records'],
              ['Custom Link Limit', 'custom_link_limit'],
              ['Priority Support Label', 'priority_support_label'],
            ].map(([label, field]) => (
              <div key={field}>
                <Label>{label}</Label>
                <Input
                  type={['monthly_price', 'yearly_price', 'trial_days', 'product_limit', 'invoice_limit_per_month', 'customer_limit', 'staff_limit', 'max_branches', 'max_transactions_per_month', 'max_loans', 'max_credit_records', 'custom_link_limit'].includes(field) ? 'number' : 'text'}
                  value={form[field]}
                  onChange={e => setForm(prev => ({ ...prev, [field]: ['monthly_price', 'yearly_price', 'trial_days', 'product_limit', 'invoice_limit_per_month', 'customer_limit', 'staff_limit', 'max_branches', 'max_transactions_per_month', 'max_loans', 'max_credit_records', 'custom_link_limit'].includes(field) ? Number(e.target.value) : e.target.value }))}
                  className="mt-1"
                />
              </div>
            ))}
            {[
              ['Default package', 'is_default'],
              ['Package active', 'is_active'],
              ['Can create store', 'can_create_store'],
              ['Can create vCard', 'can_create_vcard'],
              ['Can create invoices', 'can_create_invoices'],
              ['Can create quotations', 'can_create_quotations'],
              ['Can use CRM', 'can_use_crm'],
              ['Can use payment reminders', 'can_use_payment_reminders'],
              ['Can use subscriptions', 'can_use_subscriptions'],
              ['Can use budget', 'can_use_budget'],
              ['Can use business profile', 'can_use_business_profile'],
              ['Can use QR codes', 'can_use_qr_codes'],
              ['Can use POS', 'can_use_pos'],
              ['Can use receipts', 'can_use_receipts'],
              ['Can use delivery options', 'can_use_delivery_options'],
              ['Can use analytics', 'can_use_analytics'],
              ['Can use staff accounts', 'can_use_staff_accounts'],
              ['Can use WhatsApp templates', 'can_use_whatsapp_templates'],
              ['Can use bulk payment links', 'can_use_bulk_payment_links'],
              ['Can use marketplace', 'can_use_marketplace'],
              ['Advanced insights enabled', 'advanced_insights_enabled'],
              ['Reports enabled', 'reports_enabled'],
              ['Export enabled', 'export_enabled'],
              ['Custom domain allowed', 'custom_domain_allowed'],
              ['Remove branding allowed', 'remove_branding_allowed'],
              ['WhatsApp orders allowed', 'whatsapp_orders_allowed'],
              ['Online payments allowed', 'online_payments_allowed'],
              ['Cash on delivery allowed', 'cash_on_delivery_allowed'],
            ].map(([label, field]) => (
              <label key={field} className="flex items-center gap-2 text-sm text-slate-600">
                <Switch checked={!!form[field]} onCheckedChange={v => setForm(prev => ({ ...prev, [field]: v }))} />
                {label}
              </label>
            ))}
            <div className="md:col-span-2 flex items-center gap-2">
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white">
                <Plus size={16} className="mr-1" /> {saving ? 'Saving...' : (editing ? 'Update Package' : 'Create Package')}
              </Button>
              {editing && <Button type="button" variant="outline" onClick={() => { setEditing(null); setForm(EMPTY); }}>Cancel</Button>}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>All Packages</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
                <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Monthly</TableHead>
                <TableHead>Yearly</TableHead>
                <TableHead>Store</TableHead>
                <TableHead>vCard</TableHead>
                <TableHead>Products</TableHead>
                <TableHead>Branches</TableHead>
                <TableHead>Txns / Mo</TableHead>
                <TableHead>Insights</TableHead>
                <TableHead>Reports</TableHead>
                <TableHead>Default</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {packages.map(pkg => (
                <TableRow key={pkg.id}>
                  <TableCell>{pkg.name}</TableCell>
                  <TableCell>{pkg.currency || 'UGX'}</TableCell>
                  <TableCell>{(pkg.currency || 'UGX')} {Number(pkg.monthly_price || 0).toLocaleString()}</TableCell>
                  <TableCell>{(pkg.currency || 'UGX')} {Number(pkg.yearly_price || 0).toLocaleString()}</TableCell>
                  <TableCell>{pkg.can_create_store ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{pkg.can_create_vcard ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{pkg.product_limit || 0}</TableCell>
                  <TableCell>{pkg.max_branches || 0}</TableCell>
                  <TableCell>{pkg.max_transactions_per_month || 0}</TableCell>
                  <TableCell>{pkg.advanced_insights_enabled ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{pkg.reports_enabled ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{pkg.is_default ? 'Yes' : 'No'}</TableCell>
                  <TableCell>{(pkg.active !== false && pkg.is_active !== false) ? 'Active' : 'Inactive'}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => edit(pkg)}>
                        <Pencil size={14} className="mr-1" /> Edit
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => disable(pkg)}>
                        <Trash2 size={14} className="mr-1" /> Disable
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
